import type { FormEvent } from "react";
import { patch } from "../api";
import { useAdminDetailFocus } from "../admin-detail-focus";
import type { Session } from "../types";
import type { MutationRunner } from "./AdminCorrections";

export interface EditSessionFields {
  title?: string;
  classDate?: string;
  startTime?: string;
  endTime?: string;
  teacherName?: string;
}

export function buildEditSessionPayload(session: Session, fields: EditSessionFields) {
  return {
    title: fields.title === undefined ? session.title : fields.title.trim(),
    classDate: fields.classDate ?? session.class_date,
    startTime: fields.startTime ?? session.start_time,
    endTime: fields.endTime ?? session.end_time,
    teacherName: fields.teacherName ?? session.teacher_name ?? "",
    reason: "Class details updated",
  };
}

export function EditClassForm({ session, working, mutate, onCancel, onSaved }: {
  session: Session;
  working: boolean;
  mutate: MutationRunner;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { containerRef, headingRef } = useAdminDetailFocus(session.session_id);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const payload = buildEditSessionPayload(session, {
      title: String(values.get("title") ?? ""),
      classDate: String(values.get("classDate") ?? ""),
      startTime: String(values.get("startTime") ?? ""),
      endTime: String(values.get("endTime") ?? ""),
      teacherName: String(values.get("teacherName") ?? ""),
    });
    const dateOrTimeChanged = payload.classDate !== session.class_date
      || payload.startTime !== session.start_time || payload.endTime !== session.end_time;
    const hasRelations = Number(session.attendance_count || 0) > 0 || Number(session.payment_count || 0) > 0;
    let acknowledgeRelatedRecords = false;
    if (dateOrTimeChanged && hasRelations) {
      acknowledgeRelatedRecords = window.confirm(
        "This class already has check-ins or payments. Changing its date or time affects related operational records. Continue?",
      );
      if (!acknowledgeRelatedRecords) return;
    }
    const result = await mutate(() => patch(`/api/admin/sessions/${session.session_id}/edit`, {
      ...payload,
      acknowledgeRelatedRecords,
    }), "Class updated");
    if (result !== undefined) onSaved();
  }

  return <section className="admin-panel admin-detail edit-class-panel" ref={containerRef} aria-labelledby="edit-class-heading">
    <h2 id="edit-class-heading" ref={headingRef} tabIndex={-1}>Edit Class</h2>
    <form className="form-grid" onSubmit={save}>
      <label><span>Class name</span><input name="title" required defaultValue={session.title} /></label>
      <label><span>Date</span><input name="classDate" type="date" required defaultValue={session.class_date} /></label>
      <label><span>Start time</span><input name="startTime" type="time" required defaultValue={session.start_time} /></label>
      <label><span>End time</span><input name="endTime" type="time" required defaultValue={session.end_time} /></label>
      <label><span>Instructor</span><input name="teacherName" maxLength={120} defaultValue={session.teacher_name || ""} /></label>
      <div className="row-actions">
        <button className="primary-button" disabled={working}>Save changes</button>
        <button type="button" className="secondary-button" disabled={working} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  </section>;
}